/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo, Fragment } from "react";
import {
  Grid,
  Box,
  Typography,
  Container,
  TextField,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Button,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { signIn } from "aws-amplify/auth";
import { RootState, useAppDispatch } from "@store/store";
import {
  set_Id_applicationCondition,
  setAccountUserId,
  setAccountUserPhone,
  setClientDetailsId,
  setCompanyData,
  setId_application,
  setLegalRepresentative,
  setLoanId,
  setMotorbikeInformation,
  setPersonalData,
} from "@store/slices/registerSlice";
import {
  useLazyGetUserQuery,
  usePatchUserMutation,
  useRegisterUserMutation,
  useRequestPhoneCodeMutation,
} from "@api/onboarding";
import { useLazyGetSessionInfoQuery } from "@api/session";
import { useLazyGetMotorbikeCatalogByIdQuery, useLazyGetPersonTypeQuery } from "@api/catalogs";
import { TerminosCondiciones } from "@components/TerminosCondiciones";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import { transformTitleCase } from "@helpers/funciones";
import {
  CatalogPersonType,
  MotorbikeCatalogItem,
  PayloadPersonPatch,
  PayloadRegisterUser,
  PayloadRequestPhoneCode,
  TaxSystemConfigResponse,
} from "@interfaces/index";
import { NAME, LAST_NAMES, PHONE_NUMBER, RFC_PFAE, RFC_PM } from "@utils/expreciones";
import "react-phone-input-2/lib/style.css";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { usePatchCuentaMutation } from "@api/account";
import { setLoanStatus } from "@store/slices/registerSlice";
import { UUID } from "crypto";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import {
  useLazyGetTaxSystemConfigQuery,
  usePostMotorbikeDetailMutation,
  usePutMotorbikeDetailMutation,
} from "@api/applications";
import { PERSON_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import {
  useLazyGetPersonByIdQuery,
  useLazyGetPersonsQuery,
  usePatchPersonMutation,
  usePostPersonMutation,
} from "@api/personasApi";

type FormValues = {
  name: string;
  first_lastname: string;
  second_lastname: string;
  country_code: string;
  phone: string;
  person_type_id: string;
  rol_detail: string;
  rfc: string;
  company_name: string;
};

const RegistroDatosPersonales = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();

  const application = useSelector((state: RootState) => state.register.application);
  const rfcApprovedCompany = useSelector((state: RootState) => state.register.rfc_approved_company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const companyData = useSelector((state: RootState) => state.register.company);
  const motorbikeInfo = useSelector((state: RootState) => state.register.motorbike);
  const RFC_REGEX = application.tax_system_type === TAX_SYSTEM_TYPE.PM.code ? RFC_PM : RFC_PFAE;
  const LABEL_RFC_INPUT =
    application.tax_system_type === TAX_SYSTEM_TYPE.PM.code ? "RFC de la empresa" : "RFC de la PFAE";

  const dispatch = useAppDispatch();
  const [registerUser] = useRegisterUserMutation();
  const [requestPhoneCode] = useRequestPhoneCodeMutation();
  const [getPersonTypesTrigger] = useLazyGetPersonTypeQuery();
  const [getSessionInfo] = useLazyGetSessionInfoQuery();
  const [patchAccount] = usePatchCuentaMutation();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getUserData] = useLazyGetUserQuery();
  const [patchUser] = usePatchUserMutation();
  const [getPersonsByLoanId] = useLazyGetPersonsQuery();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [saveLegalRepresentative] = usePostPersonMutation();
  const [patchPerson] = usePatchPersonMutation();
  const [getMotorbikeCatalogById] = useLazyGetMotorbikeCatalogByIdQuery();
  const [saveMotorbikeDetail] = usePostMotorbikeDetailMutation();
  const [putMotorbikeDetail] = usePutMotorbikeDetailMutation();

  const [isLoadingInitial, setIsloadingInitial] = useState(false);
  const [personTypeCatalog, setPersonTypeCatalog] = useState<CatalogPersonType[]>([]);
  const [otherPersonTypeId, setOtherPersonTypeId] = useState<string | null>(null);
  const [taxSystems, setTaxSystems] = useState<TaxSystemConfigResponse | null>(null);
  const [personTypeId, setPersonTypeId] = useState<string[]>([]);
  const [personId, setPersonId] = useState<string>("");
  const [motorbikeItem, setMotorbikeItem] = useState<MotorbikeCatalogItem | null>(null);

  const isPFRequest = useMemo(() => {
    return application.tax_system_type === TAX_SYSTEM_TYPE.PF.code;
  }, [application.tax_system_type]);

  const isPFAERequest = useMemo(() => {
    return application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;
  }, [application.tax_system_type]);

  const isPMRequest = useMemo(() => {
    return application.tax_system_type === TAX_SYSTEM_TYPE.PM.code;
  }, [application.tax_system_type]);

  // generar validaciones de formik basado en el tipo de solicitud
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required("Campo requerido").matches(new RegExp(NAME), "Nombre inválido, revisa el formato"),
        first_lastname: yup
          .string()
          .required("Campo requerido")
          .matches(new RegExp(LAST_NAMES), "Apellido paterno inválido, revisa el formato"),
        second_lastname: yup.string().matches(new RegExp(LAST_NAMES), "Apellido materno inválido, revisa el formato"),
        country_code: yup.string().required("Campo requerido"),
        phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
        // campos no requeridos cuando la solicitud es para PF (no usar yup.when con claves que no existen en values)
        person_type_id: isPFRequest ? yup.string().notRequired() : yup.string().required("Campo requerido"),
        rol_detail: isPFRequest
          ? yup.string().notRequired()
          : yup.string().when("person_type_id", {
              is: otherPersonTypeId,
              then: (schema) => schema.required("Campo requerido"),
            }),

        rfc: isPFRequest
          ? yup.string().notRequired()
          : yup.string().required("Campo requerido").matches(RFC_REGEX, "Formato incorrecto, inténtalo de nuevo"),
        company_name: isPFRequest ? yup.string().notRequired() : yup.string().required("Campo requerido"),
      }),
    [application.tax_system_type],
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      name: clientDetails.name || "",
      first_lastname: clientDetails.first_lastname || "",
      second_lastname: clientDetails.second_lastname || "",
      country_code: accountUser.code_phone || "+52",
      phone: accountUser.phone || "",
      person_type_id: clientDetails.person_type_id || "",
      rol_detail: clientDetails.rol_detail || "",
      rfc: companyData.rfc || "",
      company_name: companyData.name || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid) {
          return;
        }

        setSubmitting(true);

        const rfc_format = values.rfc.trim();
        const company_format = values.company_name.trim();

        if (clientDetails && clientDetails.id !== "00000000-0000-0000-0000-000000000000") {
          await patchAccount({
            accountId: clientDetails.id,
            body: {
              name: values.name,
              last_name: values.first_lastname,
              last_name_2: values.second_lastname,
              person_type_id: values.person_type_id,
              company_rfc: rfc_format,
              company_name: company_format,
            },
          }).unwrap();

          await patchUser({
            id: String(accountUser.id),
            data: {
              rol_detail: values.rol_detail,
              rol_id: values.person_type_id,
              phone: `${values.country_code}${values.phone}`,
            },
          }).unwrap();

          // crear o actualizar el representante legal
          await createPersonOrUpdatePerson(application.id, values);

          // actualizar detalle de la moto
          await createOrUpdateMotorbikeDetail(application.id);

          dispatch(
            setPersonalData({
              id: clientDetails.id,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              country_code: "",
              phone: "",
              person_type_id: values.person_type_id,
              rol_detail: values.rol_detail,
              cell_phone_verified: clientDetails.cell_phone_verified,
            }),
          );
          dispatch(
            setCompanyData({
              id: clientDetails.id,
              rfc: values.rfc,
              name: values.name,
              legal_name: "",
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_correct_ciec: false,
            }),
          );
          dispatch(
            setAccountUserPhone({
              phone_full: `${values.country_code}${values.phone}`,
              code_phone: values.country_code,
              phone: values.phone,
            }),
          );
        } else {
          const payloadRegisterUser: PayloadRegisterUser = {
            username: accountUser.email,
            email: accountUser.email,
            password: accountUser.password,
            phone: `${values.country_code}${values.phone}`,
            rol_detail: values.rol_detail,
            rol_id: values.person_type_id,
            loan_application: {
              product_id: application.product_id as UUID,
              tax_systems_id: String(application.tax_system_id),
              source_code: application.source_code,
              financed_services_id: application.financed_services_id,
              guarantee_type_id: application.guarantee_type_id,
              description_guarantee: application.description_guarantee,
              guarantee_types: application.guarantee_types?.length ? application.guarantee_types : null,
            },
            application_condition: {
              requested_amount: application.loan_amount,
              requested_term: application.loan_term,
              selected_amount: application.loan_amount,
              selected_term: application.loan_term,
              approved_max_term: taxSystems?.maxTerm,
              approved_min_term: taxSystems?.minTerm,
              loan_amount: application.loan_amount,
              approved_amount: taxSystems?.maxAmount,
            },
            client_details: {
              name: values.name,
              last_name: values.first_lastname,
              last_name_2: values.second_lastname,
              person_type_id: values.person_type_id,
              tax_systems_id: String(application.tax_system_id),
              is_previous_client: clientDetails.is_previous_client,
              company_rfc: rfc_format,
              company_name: company_format,
              // estos datos se guardan solo para solicitudes <PF>
              country_code: isPFRequest ? values.country_code : "",
              cell_phone_number: isPFRequest ? values.phone : "",
            },
          };

          const responseRegisterUser = await registerUser(payloadRegisterUser).unwrap();

          await signIn({
            username: accountUser.email,
            password: accountUser.password,
          });

          await getSessionInfo(responseRegisterUser.data.sso_id).unwrap();

          dispatch(setAccountUserId(responseRegisterUser.data.id));
          dispatch(
            setAccountUserPhone({
              phone_full: `${values.country_code}${values.phone}`,
              code_phone: values.country_code,
              phone: values.phone,
            }),
          );
          dispatch(setLoanId(responseRegisterUser.data.loan_application.id));
          dispatch(setClientDetailsId(responseRegisterUser.data.client_details.id));
          dispatch(setId_application(Number(responseRegisterUser.data.loan_application.id_application)));
          dispatch(set_Id_applicationCondition(String(responseRegisterUser.data.application_condition.id)));
          dispatch(
            setPersonalData({
              id: responseRegisterUser.data.client_details.id,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              person_type_id: values.person_type_id,
              rol_detail: values.rol_detail,
              cell_phone_verified: false,
              // estos datos se guardan solo para solicitudes <PF>
              country_code: isPFRequest ? values.country_code : "",
              phone: isPFRequest ? values.phone : "",
            }),
          );
          dispatch(
            setCompanyData({
              id: responseRegisterUser.data.client_details.id,
              rfc: values.rfc,
              name: values.company_name,
              legal_name: "",
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_correct_ciec: false,
            }),
          );

          await createPersonOrUpdatePerson(responseRegisterUser.data.loan_application.id, values);

          // guardar el detalle de la moto
          await createOrUpdateMotorbikeDetail(responseRegisterUser.data.loan_application.id);
        }

        // si la solicitud es <PF> no requiere validar el teléfono
        if (isPFRequest) {
          dispatch(setLoanStatus("CSVTE"));
          navigate("/producto/creditosimple/datos_oficiales");
          return;
        } else {
          if (!clientDetails.cell_phone_verified) {
            const payloadRequestPhoneCode: PayloadRequestPhoneCode = {
              country_code: values.country_code,
              phone_number: values.phone,
            };
            if (clientDetails.phone === "") {
              await requestPhoneCode(payloadRequestPhoneCode).unwrap();
            } else if (clientDetails.phone !== values.phone) {
              await requestPhoneCode(payloadRequestPhoneCode).unwrap();
            }
          }

          dispatch(setLoanStatus("CSVTE"));
          navigate("/producto/creditosimple/validacion_telefono");
        }
      } catch (error) {
        // TODO: validar el error de usuario registrado, viene con status 400 y code 001
        const api_error = error as ApiError;

        if (api_error.status && api_error.data) {
          if (api_error.data.code === "002") {
            formik.setFieldError("phone", "El teléfono ya se encuentra registrado, intenta con otro número");
            return;
          }

          if (api_error.data.code === "001") {
            formik.setFieldError("name", "El correo ya se encuentra registrado, intenta con otro correo");
            return;
          }

          if (api_error?.status === 429) {
            formik.setFieldError(
              "phone",
              "Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un asesor.",
            );
            return;
          }

          if (api_error.data.code === "000") {
            formik.setFieldError("rfc", `Existe una cuenta con RFC - ${values.rfc}`);
            return;
          }
        }

        Sentry.captureException(error, {
          extra: {
            component: "RegistroDatosPersonales",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsloadingInitial(true);
        if (!application.tax_system_type || !accountUser.email) {
          return;
        }

        const result = await getPersonTypesTrigger().unwrap();
        const taxSystemConfig = await getTaxSystemConfig(application.type).unwrap();

        setTaxSystems(taxSystemConfig);

        const filteredData = result
          .filter(
            (item) =>
              (item.tax_system_type === application.tax_system_type || item.tax_systems_id === null) &&
              item.use_for_rol,
          )
          .sort((a, b) => a.name.localeCompare(b.name));

        setPersonTypeCatalog(filteredData);

        const otherItem = filteredData.find((item) => item.name.toLowerCase() === "otro");
        if (otherItem) {
          setOtherPersonTypeId(otherItem.id);
        }

        if (accountUser.id !== null && accountUser.id !== 0 && accountUser.id !== undefined) {
          const userResponse = await getUserData({ id: String(accountUser.id) }).unwrap();
          formik.setFieldValue("rol_detail", userResponse.rol_detail || "");
        }

        const accionistaMayoritario = result.find((item) => item.code === PERSON_CATALOG.accionista_mayoritario.code);
        const representanteLegal = result.find((item) => item.code === PERSON_CATALOG.representante_legal.code);

        const personTypeId: string[] = [];

        if (accionistaMayoritario?.id) {
          personTypeId.push(String(accionistaMayoritario.id));
        }

        if (representanteLegal?.id) {
          personTypeId.push(String(representanteLegal.id));
        }

        // obtener la información de la moto
        const motorbikeItemResponse = motorbikeInfo.motorbikeCatalogId
          ? await getMotorbikeCatalogById(motorbikeInfo.motorbikeCatalogId).unwrap()
          : null;

        setPersonTypeId(personTypeId);
        setMotorbikeItem(motorbikeItemResponse);

        // Si la solicitud es <PF> la persona debe ser de tipo duenio
        if (isPFRequest) {
          const personTypeDuenio = result.find((item) => item.code === PERSON_CATALOG.dueno_pfae.code);
          formik.setFieldValue("person_type_id", personTypeDuenio?.id || "");
          formik.setFieldValue("rol_detail", "");
        }

        if (
          application.id === null ||
          application.id === "00000000-0000-0000-0000-000000000000" ||
          application.id === undefined
        ) {
          return;
        }
        const responseGetPersons = await getPersonsByLoanId({
          loanApplicationId: application.id,
        }).unwrap();

        if (
          responseGetPersons.length > 0 &&
          application.id !== "00000000-0000-0000-0000-000000000000" &&
          application.id !== undefined
        ) {
          const responseGetPerson = await getPersonById(responseGetPersons[0].id).unwrap();
          setPersonId(String(responseGetPerson.id));
        }
      } catch (error) {
        console.error("Ha ocurrido un error al consultar el catalogo", error);
      } finally {
        setIsloadingInitial(false);
      }
    };

    fetchInitialData();
  }, [application, accountUser]);

  useEffect(() => {
    formik.setFieldValue("rol_detail", "");
  }, [formik.values.person_type_id]);

  const handleUpperChange = (values: string) => {
    formik.setFieldValue("rfc", values.toUpperCase());
  };

  const createPersonOrUpdatePerson = async (loanApplicationId: string, values: FormValues) => {
    try {
      if (!isPFAERequest && !isPFRequest) {
        return;
      }

      const payLegalRepresentativeData: PayloadPersonPatch = {
        person_types: personTypeId,
        name: values.name,
        last_name: values.first_lastname,
        last_name_2: values.second_lastname,
        loan_application_id: loanApplicationId,
        code_phone_number: values.country_code,
        phone_number: values.phone,
        email: accountUser.email,
        rfc: values.rfc,
        is_shareholder: true,
      };

      if (personId) {
        await patchPerson({
          personId: personId,
          body: payLegalRepresentativeData,
        }).unwrap();
      } else {
        const responseCreate = await saveLegalRepresentative({ body: payLegalRepresentativeData }).unwrap();
        setPersonId(responseCreate.data.id);
        dispatch(setLegalRepresentative(responseCreate.data));
      }
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "CreatePersonOrUpdatePerson",
          values: values,
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    }
  };

  const createOrUpdateMotorbikeDetail = async (loanApplicationId: string) => {
    if (!motorbikeItem || !motorbikeItem.data) {
      return false;
    }

    let motorbikeDetailId = motorbikeInfo.motorbikeDetailId;

    if (motorbikeDetailId) {
      await putMotorbikeDetail({
        solicitudId: loanApplicationId,
        motorbikeDetailId: motorbikeDetailId,
        body: {
          motorbike_catalog_id: motorbikeItem.id,
          details: {
            model: motorbikeItem.data.model,
            price: motorbikeItem.data.price,
            down_payment: motorbikeItem.data.down_payment,
            daily_payment: motorbikeItem.data.daily_payment,
          },
        },
      }).unwrap();
    } else {
      const responseDetail = await saveMotorbikeDetail({
        solicitudId: loanApplicationId,
        body: {
          motorbike_catalog_id: motorbikeItem.id,
          details: {
            model: motorbikeItem.data.model,
            price: motorbikeItem.data.price,
            down_payment: motorbikeItem.data.down_payment,
            daily_payment: motorbikeItem.data.daily_payment,
          },
        },
      }).unwrap();

      motorbikeDetailId = responseDetail.id;
    }

    dispatch(setMotorbikeInformation({ motorbikeDetailId, motorbikeItem }));

    return true;
  };

  useEffect(() => {
    if (!rfcApprovedCompany) {
      return;
    }
    formik.setFieldValue("rfc", rfcApprovedCompany);
  }, [rfcApprovedCompany]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        {clientDetails && clientDetails.id !== "00000000-0000-0000-0000-000000000000" && <InactivityHandler />}
        <Box
          sx={{
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant={isMobileOrTablet ? "h4" : "h3"} fontWeight={500} textAlign="center" color="primary.dark">
            {interfaceConfig.textos_tofu?.pm?.registro_datos_personales?.titulo || "Creación de cuenta"}
          </Typography>

          <Container maxWidth="sm">
            <form onSubmit={formik.handleSubmit} noValidate>
              {!isPFRequest && (
                <Fragment>
                  <Box maxWidth={"sm"} mt={isMobileOrTablet ? 2 : 3}>
                    <Typography variant={isMobileOrTablet ? "body2" : "body1"} textAlign="center" color="primary.dark">
                      {isPMRequest
                        ? "Ingresa los datos de la empresa"
                        : "Ingresa los datos de la persona física con actividad empresarial"}
                    </Typography>
                  </Box>

                  <FormControl fullWidth>
                    <TextField
                      id="rfc"
                      name="rfc"
                      label={LABEL_RFC_INPUT}
                      margin="normal"
                      required
                      fullWidth
                      autoComplete="off"
                      onBlur={formik.handleBlur}
                      onChange={(e) => handleUpperChange(e.target.value)}
                      value={formik.values.rfc}
                      error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                      helperText={formik.touched.rfc ? formik.errors.rfc : ""}
                      inputProps={{
                        maxLength: isPMRequest ? 12 : 13,
                      }}
                      disabled={formik.isSubmitting || companyData.is_correct_ciec}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      id="company_name"
                      name="company_name"
                      label="Nombre comercial"
                      margin="normal"
                      required
                      fullWidth
                      autoComplete="off"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.company_name}
                      error={formik.touched.company_name && Boolean(formik.errors.company_name)}
                      helperText={formik.touched.company_name ? formik.errors.company_name : ""}
                      inputProps={{ maxLength: 150 }}
                      disabled={formik.isSubmitting}
                    />
                  </FormControl>

                  <Box maxWidth={"sm"} mt={1} textAlign={"center"}>
                    <Typography variant={isMobileOrTablet ? "body2" : "body1"} color="primary.dark">
                      Ingresa los datos de usuario
                    </Typography>
                  </Box>
                </Fragment>
              )}

              <FormControl fullWidth>
                <TextField
                  id="name"
                  name="name"
                  label="Nombre(s)"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("name", transformTitleCase(e.target.value))}
                  value={formik.values.name}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name ? formik.errors.name : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  id="first_lastname"
                  name="first_lastname"
                  label="Apellido paterno"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("first_lastname", transformTitleCase(e.target.value))}
                  value={formik.values.first_lastname}
                  error={formik.touched.first_lastname && Boolean(formik.errors.first_lastname)}
                  helperText={formik.touched.first_lastname ? formik.errors.first_lastname : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  id="second_lastname"
                  name="second_lastname"
                  label="Apellido materno"
                  margin="normal"
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("second_lastname", transformTitleCase(e.target.value))}
                  inputProps={{ maxLength: 100 }}
                  value={formik.values.second_lastname}
                  disabled={formik.isSubmitting}
                  error={formik.touched.second_lastname && Boolean(formik.errors.second_lastname)}
                  helperText={formik.touched.second_lastname ? formik.errors.second_lastname : ""}
                />
              </FormControl>

              {!isPFRequest && (
                <Fragment>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="person_type_id-label" required>
                      Rol en la empresa
                    </InputLabel>
                    <Select
                      labelId="person_type_id-label"
                      id="person_type_id"
                      name="person_type_id"
                      fullWidth
                      required
                      label="Rol en la empresa*"
                      value={formik.values.person_type_id}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={formik.touched.person_type_id && Boolean(formik.errors.person_type_id)}
                      disabled={formik.isSubmitting}
                    >
                      <MenuItem value={""}>Selecciona</MenuItem>
                      {personTypeCatalog.length > 0 ? (
                        personTypeCatalog.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value={""} disabled>
                          No hay opciones disponibles
                        </MenuItem>
                      )}
                    </Select>
                    {Boolean(formik.touched.person_type_id) && Boolean(formik.errors.person_type_id) && (
                      <FormHelperText>{formik.errors.person_type_id}</FormHelperText>
                    )}
                  </FormControl>

                  {formik.values.person_type_id === otherPersonTypeId && (
                    <FormControl fullWidth>
                      <TextField
                        id="rol_detail"
                        name="rol_detail"
                        label="Especifica otro"
                        margin="normal"
                        fullWidth
                        required={formik.values.person_type_id === otherPersonTypeId}
                        autoComplete="off"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.rol_detail}
                        disabled={formik.isSubmitting}
                        inputProps={{ maxLength: 150 }}
                      />
                    </FormControl>
                  )}
                </Fragment>
              )}

              <Grid container spacing={1} mt={2}>
                <Grid item xs={5} sm={5} md={5}>
                  <CustomContryCode
                    label="Código"
                    name="country_code"
                    value={formik.values.country_code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                    disabled={formik.isSubmitting || clientDetails.cell_phone_verified}
                    required={true}
                  />
                </Grid>

                <Grid item xs={7} sm={7} md={7}>
                  <TextField
                    type="text"
                    name="phone"
                    variant="outlined"
                    label="Teléfono"
                    fullWidth
                    value={formik.values.phone}
                    required
                    onChange={(event) => {
                      formik.setFieldValue("phone", event.target.value.replace(/[^0-9]/g, ""));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                    }}
                    disabled={formik.isSubmitting || clientDetails.cell_phone_verified}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting || isLoadingInitial}
                >
                  Continuar
                </Button>
              </Box>
            </form>

            <Box sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
              <TerminosCondiciones />
            </Box>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default RegistroDatosPersonales;
